// React and hooks
import React, { useState, useEffect, useRef } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Routing
import { NavLink } from 'react-router-dom';

// UI Components (from React Bootstrap)
import { Row, Col, Dropdown, Form, Button, Alert } from 'react-bootstrap';
import { AnimatePresence, motion } from 'framer-motion';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';

// Utils and error handlers
import { handleCatchError, handleAPIError } from 'utils/handleErrors';

// Custom Components
import HtmlHead from 'components/html-head/HtmlHead';
import UsersCSV from 'components/reactCsv/UsersCSV';
import AdminLoader from 'components/loading/AdminLoader';
import LoadingPulse from 'components/loading/LoadingPulse';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import { ToastErrorCath } from 'components/alerts/toast';
import Paginate from '../../components/Paginate';
import EmptyList from '../../components/EmptyList';
import UserCard from './components/UserCard';

// Redux Actions
import { setPage, setItemsPerPage, setSearch } from '../../../slices/uiStates/allUsersSlice';

// API calls
import { getUserAccounts } from '../../api/Api-admin';

const { FormatMoney } = require('format-money-js');

const Users = () => {
  const title = 'Usuarios';
  const description = 'Usuarios Plataforma Autopaquete';
  const dispatch = useDispatch();
  // - Global State from Store.js
  const { page, itemsPerPage } = useSelector((state) => state.all_users);
  // -Local States
  const [users, setUsers] = useState([]);
  const [identifierUser, setIdentifierUser] = useState('');
  // paginate states
  const [totalItems, setTotalItems] = useState(0); // para la vista total de usuarios y para el excel
  const [totalPages, setTotalPages] = useState(0); // para paginate component
  const [pageView, setPageView] = useState(page); // for the view
  const [itemsPerPageView, setItemsPerPageView] = useState(itemsPerPage); // for the view
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoader, setIsInitialLoader] = useState(true);
  const [dismissingAlertShow, setDismissingAlertShow] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const windowSize = useWindowSize();
  const fullHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight, windowSize.height);
  const alertVariants = {
    hidden: { x: 100, opacity: 0 }, // Aparece desde la derecha
    visible: { x: 0, opacity: 1 }, // Posición final (en pantalla)
    exit: { x: -100, opacity: 0 }, // Sale hacia la izquierda
  };
  // - Use references to do scroll Up
  const startRef = useRef(null);
  // Get Shippings
  const fm = new FormatMoney({
    decimals: 0, // No decimales
  });
  const cardVariants = {
    hidden: { opacity: 0, y: 50 }, // Empieza invisible y desplazado hacia abajo
    visible: { opacity: 1, y: 0 }, // Se vuelve visible y se posiciona
  };
  const handlerGetUsers = async () => {
    try {
      setIsLoading(true);
      const response = await getUserAccounts(page, itemsPerPage);
      // console.log('all users res:', response);
      if (response.total_items !== undefined) {
        setUsers(response.data);
        setTotalPages(response.total_pages);
        setTotalItems(response.total_items);
        // REDUX VALUE UPDATE
        // dispatch(setItemsPerPage(response.page_size));
        // dispatch(setPage(response.current_page));
        // LOCAL VIEW VALUES UPDATE
        setPageView(response.current_page);
        setItemsPerPageView(response.page_size);

        if (startRef.current) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        // setIsLoading(false);
      } else if (response.error) {
        setUsers([]); // It is necessary to avoid the error  - users.map is not a function
        handleAPIError(response);
      }
    } catch (error) {
      handleCatchError(error);
      setUsers([]);
    } finally {
      setIsLoading(false);
      setIsInitialLoader(false);
    }
  };
  const handlerGetUserByIdentifier = async () => {
    try {
      setIsLoading(true);
      const response = await getUserAccounts(1, itemsPerPage, identifierUser);
      // console.log('identifier users res:', response);
      if (response.data !== undefined) {
        setUsers(response.data);
        setTotalPages(response.total_pages);
        setTotalItems(response.total_items);
        //  setIsLoading(false);
      } else if (response.error) {
        setUsers([]); // It is necessary to avoid the error  - users.map is not a function
        setTotalPages(0);
        handleAPIError(response);
      }
    } catch (error) {
      handleCatchError(error);
      setUsers([]);
    } finally {
      setIsLoading(false);
      setIsInitialLoader(false);
    }
  };

  const settingPage = (record) => {
    setIsLoading(true);
    dispatch(setPage(1));
    dispatch(setItemsPerPage(record));
  };
  const resetInitialValues = () => {
    setIdentifierUser('');
    handlerGetUsers();
  };
  useEffect(() => {
    if (identifierUser) {
      handlerGetUserByIdentifier();
    } else {
      handlerGetUsers();
    }
  }, [page, itemsPerPage]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handlerGetUserByIdentifier();
    }
  };
  const searchHandler = () => {
    if (identifierUser) {
      handlerGetUserByIdentifier();
    } else {
      ToastErrorCath('Debes proporcionar el nombre o CRM del usuario antes de realizar la búsqueda.');
    }
  };
  setTimeout(() => {
    if (showConfetti) {
      setShowConfetti(false);
    }
  }, 60000); // 2 minutos
  return (
    <>
      {isLoading && <LoadingPulse />}
      {showConfetti && (
        <Confetti
          numberOfPieces={200}
          width={windowSize.width}
          height={fullHeight}
          run
          colors={['#f8a29e', '#f25460', '#91dacb', '#3d3e40', '#FFD700', '#FFEA00', '#00BFFF', '#32CD32', '#FF69B4', '#FF8C00']}
        />
      )}
      {isInitialLoader ? (
        <AdminLoader />
      ) : (
        <>
          <HtmlHead title={title} description={description} />
          <div className="page-title-container" ref={startRef}>
            <Row className="g-0">
              {/* Title Start */}
              <Col className="col-auto mb-3 mb-sm-0 me-auto order-1">
                <NavLink to="/" className="invisible">
                  <CsLineIcons icon="chevron-left" size="13" />
                  <span className="align-middle text-small ms-1">Inicio</span>
                </NavLink>
                <h1 className="mb-0 pb-0 display-4" id="title">
                  {title}
                </h1>
              </Col>
              {/* Title End */}
            </Row>
          </div>
          {/* *** Dropdown and Search Buttons Start *** */}
          <div>
            <AnimatePresence>
              {dismissingAlertShow && (
                <motion.div key="custom-alert" initial="hidden" animate="visible" exit="exit" variants={alertVariants} transition={{ duration: 0.4 }}>
                  <Alert className="mb-2" variant="info" onClose={() => setDismissingAlertShow(false)} dismissible>
                    <span role="img" aria-label="estrella">
                      ✨
                    </span>{' '}
                    <strong>¡Nuevo!</strong> Ahora puedes descargar todos los usuarios en un archivo <strong>Excel</strong> directamente desde aquí.
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Row className="g-0 mb-3 mt-sm-5">
            {/* Search With ID Start */}
            <Col xs="auto" md="auto" className="mb-2 me-2">
              <div className="d-inline-block float-md-start pe-1 search-input-container  shadow bg-foreground me-3 mb-2" style={{ width: '274px' }}>
                <Form.Control
                  type="text"
                  style={{ fontFamily: 'Montserrat' }}
                  placeholder="ID del usuario o nombre "
                  value={identifierUser}
                  onKeyDown={handleKeyDown}
                  onChange={(e) => {
                    setIdentifierUser(e.target.value);
                  }}
                />
                {!identifierUser ? (
                  <span className="search-magnifier-icon">
                    <CsLineIcons icon="search" />
                  </span>
                ) : (
                  <span className="search-delete-icon" onClick={() => resetInitialValues()}>
                    <CsLineIcons icon="close" />
                  </span>
                )}
              </div>
              <Button variant="primary" style={{ height: '36px', width: 'auto' }} onClick={searchHandler}>
                <span>Buscar</span>
              </Button>
            </Col>
            {/* Search With ID End */}
          </Row>
          {/* este div es para ocultar el contenido cuando esta cargando  , position: 'absolute', zIndex: '1' */}
          <div style={{ minHeight: '50vh' }} id="cardContainer">
            <Row className="g-0 d-flex justify-content-end aling-items-center">
              {/* Dropdown Start */}
              <Col xs="auto" className="p-0 ms-2">
                {users.length > 9 && <UsersCSV total={totalItems} />}
                <Dropdown align={{ xs: 'end' }} className={`ms-2 d-inline-block ${users?.length < 10 && 'invisible'}`}>
                  <Dropdown.Toggle variant="foreground-alternate" className="shadow sw-18">
                    {`${itemsPerPageView} Usuarios`}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="shadow dropdown-menu-end">
                    <Dropdown.Item className={`${itemsPerPageView === 10 && 'd-none'}`} onClick={() => settingPage(10)}>
                      10 Usuarios
                    </Dropdown.Item>
                    <Dropdown.Item className={`${itemsPerPageView === 20 && 'd-none'}`} onClick={() => settingPage(20)}>
                      20 Usuarios
                    </Dropdown.Item>
                    <Dropdown.Item className={`${itemsPerPageView === 50 && 'd-none'}`} onClick={() => settingPage(50)}>
                      50 Usuarios
                    </Dropdown.Item>
                    <Dropdown.Item className={`${itemsPerPageView === 100 && 'd-none'}`} onClick={() => settingPage(100)}>
                      100 Usuarios
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              {/* Dropdown Start */}
            </Row>
            {/* *** Dropdown and Search Buttons End *** */}
            <hr className="mt-3 mb-4 mt-xl-5 bg-muted" />
            <div className={`d-flex justify-content-between mb-3 text-muted ${users?.length < 2 && 'd-none'}`}>
              <span>
                Total de usuarios <strong>{fm.from(totalItems)}</strong>
              </span>
              <span>
                Página <strong>{pageView}</strong>
              </span>
            </div>
            {/* *** Main Page Start *** */}
            {/* List Header End */}
            <Row className="g-0 mb-2 d-none d-lg-flex py-4 py-lg-0 ps-5 pe-4 custom-sort h-100">
              <Col lg="1" className="d-flex flex-column mb-lg-0 pe-3 d-flex">
                <div className="text-muted text-small cursor-pointer">CRM ID</div>
              </Col>
              <Col lg="4" className="d-flex flex-column pe-1 justify-content-center">
                <div className="text-muted text-small cursor-pointer">USUARIO</div>
              </Col>
              <Col lg="1" className="d-flex flex-column mb-lg-0 pe-3 d-flex">
                <div className="text-muted text-small cursor-pointer">ASESOR</div>
              </Col>
              <Col lg="1" className="d-flex flex-column mb-lg-0 pe-3 d-flex">
                <div className="text-muted text-small cursor-pointer">TIPO</div>
              </Col>
              <Col lg="2" className="d-flex flex-column pe-1 justify-content-center">
                <div className="text-muted text-small cursor-pointer">STATUS</div>
              </Col>
              <Col lg="2" className="d-flex flex-column pe-1 justify-content-center">
                <div className="text-muted text-small cursor-pointer">TARIFARIO</div>
              </Col>
              <Col lg="1" className=" pe-1 text-end">
                <div className="text-muted text-small">SALDO</div>
              </Col>
            </Row>
            {/* List Header End */}
            {/* User List Start */}
            {users?.length === 0 ? (
              <EmptyList message="No se encontraron registros" />
            ) : (
              <AnimatePresence>
                {users?.map((shipment, index) => (
                  <motion.div
                    key={index}
                    initial="hidden"
                    animate="visible"
                    transition={{
                      duration: 0.5,
                      delay: index * 0.2, // Retraso para animación secuencial
                    }}
                    variants={cardVariants}
                  >
                    <UserCard key={index} index={index} data={shipment} />
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
            {/* User List End */}
            {/* Pagination Start */}
            <div className={`d-flex flex-column justify-content-center align-items-center mt-5 mx-auto ${totalPages < 2 && 'd-none'}`}>
              <Paginate currentPage={pageView} totalPages={totalPages} setPageReducer={setPage} />
            </div>
            {/* Pagination End */}
          </div>
          {/* *** Main Page End *** */}
        </>
      )}
    </>
  );
};

export default Users;
